/**
 * DealShaq Shopping Assistant (DSA) — Translation tables
 *
 * Extracted from DealShaqAgent.js to keep static translation strings
 * isolated from the component's runtime logic. This isolation reduces
 * the blast radius of global text replacements and makes per-locale
 * review tractable.
 *
 * Behavior is identical to the inline version that previously lived in
 * DealShaqAgent.js — do not change strings here without also updating
 * the matching welcome message bodies in DealShaqAgent.js.
 */

// Map global LanguageContext ISO codes (en, es, ko, zh, ar, ...) to this
// module's legacy translation keys ('english', 'spanish', 'korean', ...).
// Source of truth for language selection is the header language selector,
// which updates LanguageContext.locale.
export const ISO_TO_LEGACY = {
  en: 'english',
  es: 'spanish',
  fr: 'french',
  de: 'german',
  it: 'italian',
  pt: 'portuguese',
  ko: 'korean',
  zh: 'chinese',
  ar: 'arabic',
  ja: 'japanese',
  hi: 'hindi'
};

export const UI_TRANSLATIONS = {
  english: {
    headerSubtitle: "Your food shopping companion",
    continueFromLastRequest: "Continue from your last request?",
    itemsWereSuggested: "items were suggested",
    continue: "Continue",
    dismiss: "Dismiss",
    thinking: "Thinking...",
    searchingNearbyStores: "Searching nearby stores...",
    askMeAnything: "Ask me anything about food, recipes, or deals...",
    iRememberConversation: "I remember our conversation and can help with follow-up questions",
    yesProceed: "Yes, proceed",
    noLetMeClarify: "No, let me clarify",
    searchOtherStores: "Search Other Stores",
    noThanks: "No thanks",
    readyToAdd: "Ready to Add",
    addAll: "Add all",
    whatLookingFor: "What's on your agenda today?",
    iCanHelpYou: "Among the things I can help you find include:",
    findIngredients: "Great recipes for almost any cuisine including calculating ingredient quantities",
    discoverDeals: "All sizzling hot deals at grocers near you",
    shareTips: "All grocer sizzling hot deals on your favorite items",
    findShoppingList: "All sizzling hot deals across grocers on your shopping list items",
    // Recipe-related translations
    course: "Course",
    appetizer: "Appetizer",
    soup: "Soup",
    salad: "Salad",
    fish: "Fish",
    pasta: "Pasta",
    riceRisotto: "Rice/Risotto",
    vegetable: "Vegetable",
    palateCleanser: "Palate Cleanser",
    main: "Main",
    cheese: "Cheese",
    dessert: "Dessert",
    drinkDigestif: "Drinks/Digestif",
    availableInventory: "Available Inventory",
    ingredientsCouldntSource: "Ingredients We Couldn't Source:",
    soldOut: "SOLD OUT",
    available: "available",
    addToCart: "Add to Cart",
    added: "Added"
  },
  spanish: {
    headerSubtitle: "Tu compañero de compras de alimentos",
    continueFromLastRequest: "¿Continuar desde tu última solicitud?",
    itemsWereSuggested: "artículos fueron sugeridos",
    continue: "Continuar",
    dismiss: "Descartar",
    thinking: "Pensando...",
    searchingNearbyStores: "Buscando tiendas cercanas...",
    askMeAnything: "Pregúntame sobre comida, recetas o ofertas...",
    iRememberConversation: "Recuerdo nuestra conversación y puedo ayudar con preguntas de seguimiento",
    yesProceed: "Sí, continuar",
    noLetMeClarify: "No, aclarar",
    searchOtherStores: "Buscar otras tiendas",
    noThanks: "No, gracias",
    readyToAdd: "Listo para agregar",
    addAll: "Agregar todo",
    whatLookingFor: "¿Qué estás buscando hoy?",
    iCanHelpYou: "Puedo ayudarte a:",
    findIngredients: "Encontrar ingredientes y calcular cantidades para recetas",
    discoverDeals: "Descubrir qué está en oferta en tiendas cercanas",
    shareTips: "Compartir consejos sobre almacenamiento, frescura y cocina",
    findShoppingList: "Encontrar los artículos de tu lista de compras en varias tiendas",
    // Recipe-related translations
    course: "Plato",
    appetizer: "Aperitivo",
    soup: "Sopa",
    salad: "Ensalada",
    fish: "Pescado",
    pasta: "Pasta",
    riceRisotto: "Arroz/Risotto",
    vegetable: "Verdura",
    palateCleanser: "Limpiador de Paladar",
    main: "Principal",
    cheese: "Queso",
    dessert: "Postre",
    drinkDigestif: "Bebidas/Digestivo",
    availableInventory: "Inventario Disponible",
    ingredientsCouldntSource: "Ingredientes que no pudimos encontrar:",
    soldOut: "AGOTADO",
    available: "disponible",
    addToCart: "Agregar al Carrito",
    added: "Agregado"
  },
  french: {
    headerSubtitle: "Votre compagnon d'achats alimentaires",
    continueFromLastRequest: "Continuer à partir de votre dernière demande?",
    itemsWereSuggested: "articles ont été suggérés",
    continue: "Continuer",
    dismiss: "Ignorer",
    thinking: "Réflexion...",
    searchingNearbyStores: "Recherche de magasins à proximité...",
    askMeAnything: "Demandez-moi n'importe quoi sur la nourriture, les recettes ou les offres...",
    iRememberConversation: "Je me souviens de notre conversation et peux vous aider avec des questions de suivi",
    yesProceed: "Oui, continuer",
    noLetMeClarify: "Non, clarifier",
    searchOtherStores: "Chercher d'autres magasins",
    noThanks: "Non merci",
    readyToAdd: "Prêt à ajouter",
    addAll: "Tout ajouter",
    whatLookingFor: "Que cherchez-vous aujourd'hui?",
    iCanHelpYou: "Je peux vous aider à:",
    findIngredients: "Trouver des ingrédients et calculer les quantités pour les recettes",
    discoverDeals: "Découvrir ce qui est en vente dans les magasins près de chez vous",
    shareTips: "Partager des conseils sur le stockage, la fraîcheur et la cuisine",
    findShoppingList: "Trouver vos articles de liste de courses dans plusieurs magasins",
    // Recipe-related translations
    course: "Plat",
    appetizer: "Entrée",
    soup: "Soupe",
    salad: "Salade",
    fish: "Poisson",
    pasta: "Pâtes",
    riceRisotto: "Riz/Risotto",
    vegetable: "Légume",
    palateCleanser: "Trou Normand",
    main: "Principal",
    cheese: "Fromage",
    dessert: "Dessert",
    drinkDigestif: "Boissons/Digestif",
    availableInventory: "Inventaire Disponible",
    ingredientsCouldntSource: "Ingrédients non trouvés:",
    soldOut: "ÉPUISÉ",
    available: "disponible",
    addToCart: "Ajouter au Panier",
    added: "Ajouté"
  },
  german: {
    headerSubtitle: "Ihr Einkaufsbegleiter für Lebensmittel",
    continueFromLastRequest: "Von Ihrer letzten Anfrage fortfahren?",
    itemsWereSuggested: "Artikel wurden vorgeschlagen",
    continue: "Fortfahren",
    dismiss: "Verwerfen",
    thinking: "Nachdenken...",
    searchingNearbyStores: "Suche nach Geschäften in der Nähe...",
    askMeAnything: "Fragen Sie mich alles über Essen, Rezepte oder Angebote...",
    iRememberConversation: "Ich erinnere mich an unser Gespräch und kann bei Folgefragen helfen",
    yesProceed: "Ja, weiter",
    noLetMeClarify: "Nein, klären",
    searchOtherStores: "Andere Geschäfte suchen",
    noThanks: "Nein danke",
    readyToAdd: "Bereit zum Hinzufügen",
    addAll: "Alle hinzufügen",
    whatLookingFor: "Was suchen Sie heute?",
    iCanHelpYou: "Ich kann Ihnen helfen:",
    findIngredients: "Zutaten finden und Mengen für Rezepte berechnen",
    discoverDeals: "Entdecken Sie, was in Geschäften in Ihrer Nähe im Angebot ist",
    shareTips: "Tipps zur Lagerung, Frische und zum Kochen teilen",
    findShoppingList: "Finden Sie Ihre Einkaufslistenartikel in mehreren Geschäften",
    // Recipe-related translations
    course: "Gang",
    appetizer: "Vorspeise",
    soup: "Suppe",
    salad: "Salat",
    fish: "Fisch",
    pasta: "Pasta",
    riceRisotto: "Reis/Risotto",
    vegetable: "Gemüse",
    palateCleanser: "Gaumenschmeichler",
    main: "Hauptgericht",
    cheese: "Käse",
    dessert: "Nachtisch",
    drinkDigestif: "Getränke/Digestif",
    availableInventory: "Verfügbarer Bestand",
    ingredientsCouldntSource: "Nicht gefundene Zutaten:",
    soldOut: "AUSVERKAUFT",
    available: "verfügbar",
    addToCart: "In den Warenkorb",
    added: "Hinzugefügt"
  },
  italian: {
    headerSubtitle: "Il tuo compagno per la spesa alimentare",
    continueFromLastRequest: "Continuare dalla tua ultima richiesta?",
    itemsWereSuggested: "articoli sono stati suggeriti",
    continue: "Continua",
    dismiss: "Ignora",
    thinking: "Pensando...",
    searchingNearbyStores: "Ricerca negozi vicini...",
    askMeAnything: "Chiedimi qualsiasi cosa su cibo, ricette o offerte...",
    iRememberConversation: "Ricordo la nostra conversazione e posso aiutare con domande di follow-up",
    yesProceed: "Sì, procedi",
    noLetMeClarify: "No, chiarire",
    searchOtherStores: "Cerca altri negozi",
    noThanks: "No grazie",
    readyToAdd: "Pronto da aggiungere",
    addAll: "Aggiungi tutto",
    whatLookingFor: "Cosa stai cercando oggi?",
    iCanHelpYou: "Posso aiutarti a:",
    findIngredients: "Trovare ingredienti e calcolare quantità per ricette",
    discoverDeals: "Scoprire cosa è in offerta nei negozi vicini",
    shareTips: "Condividere consigli su conservazione, freschezza e cucina",
    findShoppingList: "Trovare gli articoli della tua lista della spesa in più negozi",
    // Recipe-related translations
    course: "Portata",
    appetizer: "Antipasto",
    soup: "Zuppa",
    salad: "Insalata",
    fish: "Pesce",
    pasta: "Pasta",
    riceRisotto: "Riso/Risotto",
    vegetable: "Verdura",
    palateCleanser: "Sorbetto",
    main: "Principale",
    cheese: "Formaggio",
    dessert: "Dolce",
    drinkDigestif: "Bevande/Digestivo",
    availableInventory: "Inventario Disponibile",
    ingredientsCouldntSource: "Ingredienti non trovati:",
    soldOut: "ESAURITO",
    available: "disponibile",
    addToCart: "Aggiungi al Carrello",
    added: "Aggiunto"
  },
  portuguese: {
    headerSubtitle: "Seu companheiro de compras de alimentos",
    continueFromLastRequest: "Continuar a partir do seu último pedido?",
    itemsWereSuggested: "itens foram sugeridos",
    continue: "Continuar",
    dismiss: "Descartar",
    thinking: "Pensando...",
    searchingNearbyStores: "Procurando lojas próximas...",
    askMeAnything: "Pergunte-me qualquer coisa sobre comida, receitas ou ofertas...",
    iRememberConversation: "Lembro-me da nossa conversa e posso ajudar com perguntas de acompanhamento",
    yesProceed: "Sim, continuar",
    noLetMeClarify: "Não, esclarecer",
    searchOtherStores: "Procurar outras lojas",
    noThanks: "Não, obrigado",
    readyToAdd: "Pronto para adicionar",
    addAll: "Adicionar tudo",
    whatLookingFor: "O que você está procurando hoje?",
    iCanHelpYou: "Posso ajudá-lo a:",
    findIngredients: "Encontrar ingredientes e calcular quantidades para receitas",
    discoverDeals: "Descobrir o que está em promoção nas lojas perto de você",
    shareTips: "Compartilhar dicas sobre armazenamento, frescor e culinária",
    findShoppingList: "Encontrar os itens da sua lista de compras em várias lojas",
    // Recipe-related translations
    course: "Prato",
    appetizer: "Aperitivo",
    soup: "Sopa",
    salad: "Salada",
    fish: "Peixe",
    pasta: "Massa",
    riceRisotto: "Arroz/Risoto",
    vegetable: "Legume",
    palateCleanser: "Limpador de Paladar",
    main: "Principal",
    cheese: "Queijo",
    dessert: "Sobremesa",
    drinkDigestif: "Bebidas/Digestivo",
    availableInventory: "Inventário Disponível",
    ingredientsCouldntSource: "Ingredientes não encontrados:",
    soldOut: "ESGOTADO",
    available: "disponível",
    addToCart: "Adicionar ao Carrinho",
    added: "Adicionado"
  },
  korean: {
    headerSubtitle: "당신의 식품 쇼핑 동반자",
    continueFromLastRequest: "마지막 요청에서 계속하시겠습니까?",
    itemsWereSuggested: "개 항목이 제안되었습니다",
    continue: "계속",
    dismiss: "닫기",
    thinking: "생각 중...",
    searchingNearbyStores: "주변 상점 검색 중...",
    askMeAnything: "음식, 레시피 또는 할인에 대해 무엇이든 물어보세요...",
    iRememberConversation: "대화 내용을 기억하고 후속 질문에 도움을 드릴 수 있습니다",
    yesProceed: "네, 진행하세요",
    noLetMeClarify: "아니요, 명확히 할게요",
    searchOtherStores: "다른 상점 검색",
    noThanks: "괜찮습니다",
    readyToAdd: "추가 준비됨",
    addAll: "모두 추가",
    whatLookingFor: "오늘 무엇을 찾고 계신가요?",
    iCanHelpYou: "제가 도와드릴 수 있는 것들:",
    findIngredients: "거의 모든 요리의 훌륭한 레시피와 재료 수량 계산",
    discoverDeals: "주변 식료품점의 모든 뜨거운 할인",
    shareTips: "좋아하는 품목에 대한 모든 식료품점의 뜨거운 할인",
    findShoppingList: "쇼핑 목록 품목에 대한 모든 식료품점의 뜨거운 할인",
    // Recipe-related translations
    course: "코스",
    appetizer: "애피타이저",
    soup: "수프",
    salad: "샐러드",
    fish: "생선",
    pasta: "파스타",
    riceRisotto: "밥/리조또",
    vegetable: "채소",
    palateCleanser: "입가심",
    main: "메인",
    cheese: "치즈",
    dessert: "디저트",
    drinkDigestif: "음료/디저트 주류",
    availableInventory: "재고 있음",
    ingredientsCouldntSource: "찾을 수 없는 재료:",
    soldOut: "품절",
    available: "사용 가능",
    addToCart: "장바구니에 추가",
    added: "추가됨"
  },
  chinese: {
    headerSubtitle: "您的食品购物伙伴",
    continueFromLastRequest: "从您上次的请求继续?",
    itemsWereSuggested: "项已推荐",
    continue: "继续",
    dismiss: "忽略",
    thinking: "思考中...",
    searchingNearbyStores: "正在搜索附近商店...",
    askMeAnything: "有关食物、食谱或优惠,尽管问我...",
    iRememberConversation: "我记得我们的对话,可以回答后续问题",
    yesProceed: "是的,继续",
    noLetMeClarify: "不,让我澄清",
    searchOtherStores: "搜索其他商店",
    noThanks: "不用了,谢谢",
    readyToAdd: "准备添加",
    addAll: "全部添加",
    whatLookingFor: "您今天在找什么?",
    iCanHelpYou: "我可以帮助您:",
    findIngredients: "几乎任何菜系的精彩食谱,包括计算食材用量",
    discoverDeals: "您附近所有食品店的火热优惠",
    shareTips: "所有食品店针对您喜爱商品的火热优惠",
    findShoppingList: "所有食品店针对您购物清单商品的火热优惠",
    // Recipe-related translations
    course: "道菜",
    appetizer: "开胃菜",
    soup: "汤",
    salad: "沙拉",
    fish: "鱼",
    pasta: "意大利面",
    riceRisotto: "米饭/烩饭",
    vegetable: "蔬菜",
    palateCleanser: "清口菜",
    main: "主菜",
    cheese: "奶酪",
    dessert: "甜点",
    drinkDigestif: "饮料/餐后酒",
    availableInventory: "可用库存",
    ingredientsCouldntSource: "我们无法找到的食材:",
    soldOut: "售罄",
    available: "可用",
    addToCart: "加入购物车",
    added: "已添加"
  },
  arabic: {
    headerSubtitle: "رفيقك لتسوق الطعام",
    continueFromLastRequest: "هل تريد المتابعة من طلبك الأخير؟",
    itemsWereSuggested: "عناصر تم اقتراحها",
    continue: "متابعة",
    dismiss: "تجاهل",
    thinking: "جارٍ التفكير...",
    searchingNearbyStores: "جارٍ البحث عن المتاجر القريبة...",
    askMeAnything: "اسألني أي شيء عن الطعام أو الوصفات أو العروض...",
    iRememberConversation: "أتذكر محادثتنا ويمكنني المساعدة في أسئلة المتابعة",
    yesProceed: "نعم، تابع",
    noLetMeClarify: "لا، دعني أوضح",
    searchOtherStores: "ابحث في متاجر أخرى",
    noThanks: "لا شكرًا",
    readyToAdd: "جاهز للإضافة",
    addAll: "أضف الكل",
    whatLookingFor: "عمَّ تبحث اليوم؟",
    iCanHelpYou: "من بين الأشياء التي يمكنني مساعدتك في إيجادها:",
    findIngredients: "وصفات رائعة لأي مطبخ تقريبًا بما في ذلك حساب كميات المكونات",
    discoverDeals: "جميع العروض الساخنة في محلات البقالة القريبة منك",
    shareTips: "جميع العروض الساخنة لمحلات البقالة على العناصر المفضلة لديك",
    findShoppingList: "جميع العروض الساخنة عبر محلات البقالة على عناصر قائمة التسوق",
    // Recipe-related translations
    course: "طبق",
    appetizer: "مقبلات",
    soup: "حساء",
    salad: "سلطة",
    fish: "سمك",
    pasta: "معكرونة",
    riceRisotto: "أرز/ريزوتو",
    vegetable: "خضار",
    palateCleanser: "منعش الحنك",
    main: "طبق رئيسي",
    cheese: "جبن",
    dessert: "حلوى",
    drinkDigestif: "مشروبات/مشروب بعد الطعام",
    availableInventory: "المخزون المتوفر",
    ingredientsCouldntSource: "مكونات لم نتمكن من توفيرها:",
    soldOut: "نفدت الكمية",
    available: "متوفر",
    addToCart: "أضف إلى السلة",
    added: "مضاف"
  }
};
